/**
 * Composable for generating cross-site URLs in a multi-tenant environment
 * Handles domain resolution for different environments (local, staging, production)
 */
export const useCrossSiteUrl = () => {
  const runtimeConfig = useRuntimeConfig()

  /**
   * Generates an absolute URL for a different site/tenant
   * @param organization - The organization name to determine target site
   * @param langcode - Language code for the URL
   * @param path - The path/alias to append
   * @returns Complete absolute URL
   */
  const getCrossSiteUrl = (organization: string, langcode: string, path: string): string => {
    // Determine target site based on organization
    const targetSite = organization === "Kummler+Matter EVT AG" ? "kuma" : "equans"

    // Get current host to determine environment
    const currentHost: string = process.client ? window.location.host : runtimeConfig.requestHost || ''

    let targetDomain: string

    // Production domains (hardcoded as per config)
    if (currentHost.includes('equans.ch') || currentHost.includes('kuma-evt.ch')) {
      targetDomain = targetSite === 'kuma' ? 'https://kuma-evt.ch' : 'https://equans.ch'
    }
    // Local development (ddev)
    else if (currentHost.includes('.ddev.site')) {
      targetDomain = `https://${targetSite}.ddev.site`
    }
    // Platform.sh staging/preview environments
    else if (currentHost.includes('.platformsh.site')) {
      // Extract the platform.sh environment identifier
      const platformshPattern = /^([^.]+)\.(pr-\d+-[^-]+-[^.]+)\.ch-1\.platformsh\.site$/
      const match = currentHost.match(platformshPattern)

      if (match) {
        const envId = match[2] // e.g., "pr-123-xxxxxxx-yyyyyyyyyyyyy"
        targetDomain = `https://${targetSite}.${envId}.ch-1.platformsh.site`
      } else {
        // Fallback for main branch or other platform.sh patterns
        const fallbackPattern = /^[^.]+\.([^.]+\.ch-1\.platformsh\.site)$/
        const fallbackMatch = currentHost.match(fallbackPattern)
        if (fallbackMatch) {
          targetDomain = `https://${targetSite}.${fallbackMatch[1]}`
        } else {
          // Ultimate fallback
          targetDomain = `https://${targetSite}.main-bvxea6i-yaugup2ejykzs.ch-1.platformsh.site`
        }
      }
    }
    // Fallback for unknown environments
    else {
      targetDomain = `https://${targetSite}.ddev.site`
    }

    // Clean up the path (ensure it starts with /)
    const cleanPath = path.startsWith('/') ? path : `/${path}`

    // Build the complete URL with language prefix
    return `${targetDomain}/${langcode}${cleanPath}`
  }

  /**
   * Determines if a location requires a cross-site URL
   * @param organization - The organization name
   * @returns True if this organization should use a different domain
   */
  const requiresCrossSiteUrl = (organization: string): boolean => {
    return organization === "Kummler+Matter EVT AG"
  }

  return {
    getCrossSiteUrl,
    requiresCrossSiteUrl
  }
}
